const EXPIRED = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';

/**
 * Browser SDK 1.x and 2.x kept Amplitude's identity (AMP_<key>) and web attribution (AMP_MKTG_<key>)
 * in cookies scoped to the top-level domain, i.e. .purestorage.com, so they ride along on every
 * request to every purestorage.com host and count towards Kong's header limit (CLOUD-278099).
 * Once the SDK is switched to localStorage it never reads or removes those cookies again, so
 * they would linger until their 365-day expiry.
 *
 * This copies each cookie into storage under the same key, in the format the SDK reads there, so
 * the device id, session, user id and stored campaign carry over; then it expires the cookies.
 * Call it before the SDK loads.
 */
export function migrateLegacyAmplitudeCookies(
    doc: Document,
    storage: Pick<Storage, 'getItem' | 'setItem'>,
    apiKey: string,
): void {
    const suffix = apiKey.substring(0, 10);
    const names = [`AMP_${suffix}`, `AMP_MKTG_${suffix}`, `AMP_MKTG_ORIGINAL_${suffix}`];

    const cookies = new Map(
        doc.cookie
            .split(';')
            .map(c => c.trim())
            .filter(c => c.includes('='))
            .map(c => [c.substring(0, c.indexOf('=')), c.substring(c.indexOf('=') + 1)] as const),
    );
    if (!names.some(name => cookies.has(name))) {
        return;
    }

    for (const name of names) {
        const json = decodeCookieValue(cookies.get(name));
        try {
            // What is already in storage is newer than any cookie left behind
            if (json !== undefined && storage.getItem(name) === null) {
                storage.setItem(name, json);
            }
        } catch {
            // Storage unavailable: the SDK starts afresh, as it would without a cookie
        }
    }

    // The SDK picked the shortest writable parent domain, so we can't know which one it used;
    // expire on the host and on each parent domain except the bare TLD.
    const labels = doc.location.hostname.split('.');
    const domains = [''];
    for (let i = labels.length - 2; i >= 0; i--) {
        domains.push(`; domain=.${labels.slice(i).join('.')}`);
    }
    for (const name of names) {
        for (const domain of domains) {
            doc.cookie = `${name}=; ${EXPIRED}; path=/${domain}`;
        }
    }
}

// Mirrors the SDK's own decoding: btoa(encodeURIComponent(json)), possibly URL-encoded once more
function decodeCookieValue(value: string | undefined): string | undefined {
    if (!value) {
        return undefined;
    }
    for (const decode of [(v: string) => v, decodeURIComponent]) {
        try {
            const json = decodeURIComponent(atob(decode(value)));
            JSON.parse(json);
            return json;
        } catch {
            // try the next encoding
        }
    }
    return undefined;
}
